import { Box, Divider, Flex, Heading, SimpleGrid, Text } from '@chakra-ui/react';
import { Currency } from 'components/Number';
import moment from 'moment';
import PropTypes from 'prop-types';
import { formatBillingAddressLines, formatBillingContactAttention } from '../calc/billingDetails';
import { growthInvestmentLinesForDocument } from '../calc/growthInvestments';
import {
  buildSetOffTermsMemo,
  getSetOffNoticeDetails,
} from '../calc/setOffNotice';
import { isSignaturePresent } from '../calc/signatureStorage';
import { formatMonthKeyLabel, sumIncludedLineAmounts } from '../calc/suggestObligations';

const TREISAR_FROM = {
  companyName: 'Treisar Labs Inc.',
  streetLine: '151 W 26th Street',
  cityLine: 'New York, NY 10001',
  countryLine: 'US',
  footerLine: '151 W 26th Street, New York, NY 10001, US',
};

const FOOTER_TEXT_COLOR = '#7a8da8';

const PDF_PAGE_MIN_HEIGHT = '1050px';

const previewCardProps = {
  bg: 'white',
  border: '1px solid',
  borderColor: 'secondaryGray.200',
  borderRadius: '12px',
  p: 6,
  mb: 6,
  boxShadow: 'sm',
};

const pdfPreviewCardProps = {
  bg: 'white',
  p: 6,
  mb: 0,
};

function formatInvoiceDate(value) {
  return value ? moment(value).format('YYYY-MM-DD') : '—';
}

function InvoicePageFooter({ companyName, invoiceNumber }) {
  return (
    <Box pt={8} mt="auto">
      <Divider borderColor="secondaryGray.300" mb={3} />
      <Flex
        justify="space-between"
        align="center"
        fontSize="xs"
        color={FOOTER_TEXT_COLOR}
        gap={4}
      >
        <Text>
          {TREISAR_FROM.companyName} · {TREISAR_FROM.footerLine}
        </Text>
        <Text textAlign="right" whiteSpace="nowrap">
          {companyName} - {invoiceNumber}
        </Text>
      </Flex>
    </Box>
  );
}

InvoicePageFooter.propTypes = {
  companyName: PropTypes.string,
  invoiceNumber: PropTypes.string.isRequired,
};

function PreviewHeader({ title, invoiceNumber, issueDate, variant = 'invoice' }) {
  const showInvoiceNumber = variant === 'invoice';
  const showSetOffDateBelow = variant === 'setOff';

  return (
    <>
      <Flex justify="space-between" align="flex-start" mb={2}>
        <Box>
          <Heading size="lg" color="#1a2b6d">
            {title}
          </Heading>
          <Text fontSize="xs" letterSpacing="widest" color="secondaryGray.500" mt={1}>
            TREISAR LABS INC.
          </Text>
        </Box>
        {showInvoiceNumber ? (
          <Box textAlign="right">
            <Text fontSize="xs" color="secondaryGray.500" letterSpacing="widest">
              INVOICE NO.
            </Text>
            <Text fontWeight={700}>{invoiceNumber}</Text>
            <Text fontSize="sm" color="secondaryGray.600" mt={1}>
              {formatInvoiceDate(issueDate)}
            </Text>
          </Box>
        ) : null}
      </Flex>
      <Divider borderColor="#1a2b6d" borderWidth="2px" mb={showSetOffDateBelow ? 2 : 6} />
      {showSetOffDateBelow ? (
        <Flex justify="flex-end" mb={6}>
          <Text fontSize="sm" color="secondaryGray.600" fontWeight={600}>
            {formatInvoiceDate(issueDate)}
          </Text>
        </Flex>
      ) : null}
    </>
  );
}

PreviewHeader.propTypes = {
  title: PropTypes.string.isRequired,
  invoiceNumber: PropTypes.string.isRequired,
  issueDate: PropTypes.string,
  variant: PropTypes.oneOf(['invoice', 'setOff']),
};

function InvoicePage({ children, footer, pdfMode, pageBreakBefore, pageBreakAfter }) {
  const pageStyle = {
    ...(pageBreakBefore
      ? {
          pageBreakBefore: 'always',
          breakBefore: 'page',
        }
      : {}),
    ...(pageBreakAfter
      ? {
          pageBreakAfter: 'always',
          breakAfter: 'page',
        }
      : {}),
    ...(pdfMode
      ? {
          minHeight: PDF_PAGE_MIN_HEIGHT,
          display: 'flex',
          flexDirection: 'column',
        }
      : {}),
  };

  return (
    <Box style={pageStyle}>
      <Box flex="1">{children}</Box>
      {footer}
    </Box>
  );
}

InvoicePage.propTypes = {
  children: PropTypes.node.isRequired,
  footer: PropTypes.node,
  pdfMode: PropTypes.bool,
  pageBreakBefore: PropTypes.bool,
  pageBreakAfter: PropTypes.bool,
};

function InvoiceSignatureBlock({ signature }) {
  if (!isSignaturePresent(signature)) {
    return null;
  }

  return (
    <Box mt={8}>
      <Text fontSize="xs" color="secondaryGray.500" letterSpacing="widest" mb={2}>
        AUTHORIZED SIGNATURE
      </Text>
      <Box as="img" src={signature.dataUrl} alt="Signature" maxH="80px" mb={2} />
      {signature.signerName ? (
        <Text fontWeight={700}>{signature.signerName}</Text>
      ) : null}
      <Box borderBottom="2px solid" borderColor="gray.800" w="220px" mt={signature.signerName ? 2 : 0} />
    </Box>
  );
}

InvoiceSignatureBlock.propTypes = {
  signature: PropTypes.shape({
    dataUrl: PropTypes.string,
    signerName: PropTypes.string,
  }),
};

const InvoicePreview = ({
  invoiceNumber,
  invoiceDate,
  dueDate,
  billingDetails,
  lines,
  setOffEnabled,
  requestReceivedDate,
  fundingMonthKey,
  growthInvestment,
  growthInvestments,
  purchaseAmount,
  setOffCreatedDate,
  signature,
  pdfMode = false,
}) => {
  const cardProps = pdfMode ? pdfPreviewCardProps : previewCardProps;
  const totalDue = sumIncludedLineAmounts(lines);
  const includedLines = lines.filter((line) => line.included);
  const setOffIssueDate = setOffCreatedDate || invoiceDate;
  const growthInvestmentLines = growthInvestmentLinesForDocument({
    requestReceivedDate,
    fundingMonthKey,
    growthInvestment,
    growthInvestments,
    purchaseAmount,
  });
  const setOffNotice = getSetOffNoticeDetails({
    totalDue,
    growthInvestment,
    growthInvestments: growthInvestmentLines,
    purchaseAmount,
  });
  const addressLines = formatBillingAddressLines(billingDetails);
  const billingContactAttention = formatBillingContactAttention(billingDetails);
  const companyName = billingDetails?.invoiceCompanyName || '';

  const pageFooter = (
    <InvoicePageFooter companyName={companyName} invoiceNumber={invoiceNumber} />
  );

  return (
    <Box maxW={pdfMode ? '794px' : undefined}>
      <Box {...cardProps}>
        <InvoicePage
          pdfMode={pdfMode}
          pageBreakAfter={setOffEnabled && pdfMode}
          footer={pageFooter}
        >
          <PreviewHeader
            title="Invoice"
            invoiceNumber={invoiceNumber}
            issueDate={invoiceDate}
          />

          <SimpleGrid columns={3} spacing={6} mb={8} fontSize="sm">
            <Box>
              <Text fontSize="xs" color="secondaryGray.500" letterSpacing="widest" mb={2}>
                FROM
              </Text>
              <Text fontWeight={700}>{TREISAR_FROM.companyName}</Text>
              <Text>{TREISAR_FROM.streetLine}</Text>
              <Text>{TREISAR_FROM.cityLine}</Text>
              <Text>{TREISAR_FROM.countryLine}</Text>
            </Box>
            <Box>
              <Text fontSize="xs" color="secondaryGray.500" letterSpacing="widest" mb={2}>
                TO
              </Text>
              <Text fontWeight={700}>{billingDetails?.invoiceCompanyName}</Text>
              {billingContactAttention ? (
                <Text>Attn: {billingContactAttention}</Text>
              ) : null}
              {addressLines.map((line) => (
                <Text key={line}>{line}</Text>
              ))}
            </Box>
            <Box>
              <Text fontSize="xs" color="secondaryGray.500" letterSpacing="widest" mb={2}>
                DETAILS
              </Text>
              <Text>
                Invoice date: <strong>{formatInvoiceDate(invoiceDate)}</strong>
              </Text>
              <Text>
                Due date: <strong>{formatInvoiceDate(dueDate)}</strong>
              </Text>
              <Text mt={2}>
                Payment method:{' '}
                <strong>
                  {setOffEnabled ? 'Set-off against Growth Investment' : 'Wire transfer'}
                </strong>
              </Text>
            </Box>
          </SimpleGrid>

          <Box borderTop="1px solid" borderColor="secondaryGray.200" pt={4}>
            <Flex justify="space-between" fontSize="xs" color="secondaryGray.500" mb={2}>
              <Text letterSpacing="widest">ITEM</Text>
              <Text letterSpacing="widest">AMOUNT</Text>
            </Flex>
            {includedLines.map((line) => (
              <Flex key={line.id} justify="space-between" py={2} fontSize="sm">
                <Text maxW="70%">{line.description}</Text>
                <Text fontWeight={600}>
                  <Currency value={line.amount} maximumFractionDigits={2} />
                </Text>
              </Flex>
            ))}
            <Divider my={3} />
            <Flex justify="space-between" align="center">
              <Text fontWeight={800}>Total Due</Text>
              <Text fontSize="2xl" fontWeight={800} color="#1a2b6d">
                <Currency value={totalDue} maximumFractionDigits={2} />
              </Text>
            </Flex>
          </Box>

          {setOffEnabled && requestReceivedDate && (
            <Box mt={6} bg="#eef4ff" borderRadius="md" p={4}>
              <Text fontSize="xs" color="secondaryGray.500" letterSpacing="widest" mb={2}>
                TERMS &amp; MEMO
              </Text>
              <Text fontSize="sm">
                {buildSetOffTermsMemo({
                  requestReceivedDate: formatInvoiceDate(requestReceivedDate),
                  repaymentExceedsGrowthInvestment:
                    setOffNotice.repaymentExceedsGrowthInvestment,
                })}
              </Text>
            </Box>
          )}

          <InvoiceSignatureBlock signature={signature} />

        </InvoicePage>
      </Box>

      {setOffEnabled && (
        <Box
          {...cardProps}
          style={{
            pageBreakBefore: 'always',
            breakBefore: 'page',
          }}
        >
          <InvoicePage pdfMode={pdfMode} pageBreakBefore footer={pageFooter}>
            <PreviewHeader
              title="Set-Off Notice"
              invoiceNumber={invoiceNumber}
              issueDate={setOffIssueDate}
              variant="setOff"
            />
            <Text fontSize="sm" mb={4}>
              <Text as="span" fontWeight={700}>
                To:
              </Text>{' '}
              {billingDetails?.invoiceCompanyName}
              {billingContactAttention
                ? `, Attn: ${billingContactAttention}`
                : ''}
            </Text>
            <Text fontSize="sm" mb={6}>
              Treisar will set off the Payment Obligations referenced above against the Growth
              Investment requested by the Company, as detailed below:
            </Text>
            {growthInvestmentLines.map((line, index) => (
              <Flex key={`${line.fundingMonthKey}-${index}`} justify="space-between" fontSize="sm" mb={4}>
                <Text maxW="70%">
                  Growth Investment Requested – {formatMonthKeyLabel(line.fundingMonthKey)} Cohort
                  (Funding Date: {formatInvoiceDate(dueDate)})
                </Text>
                <Text fontWeight={700}>
                  <Currency value={line.growthInvestment} maximumFractionDigits={2} />
                </Text>
              </Flex>
            ))}
            <Box h={3} />
            <Flex justify="space-between" fontSize="sm" mb={4}>
              <Text maxW="70%">
                Less: Payment Obligations set off (per invoice {invoiceNumber} dated{' '}
                {formatInvoiceDate(invoiceDate)})
              </Text>
              <Text fontWeight={700} color="red.500">
                (<Currency value={totalDue} maximumFractionDigits={2} />)
              </Text>
            </Flex>
            <Divider mb={4} />
            <Flex justify="space-between" fontWeight={800}>
              <Text>{setOffNotice.netRemittanceLabel}</Text>
              <Text>
                <Currency value={setOffNotice.netRemittanceAmount} maximumFractionDigits={2} />
              </Text>
            </Flex>

          </InvoicePage>
        </Box>
      )}
    </Box>
  );
};

InvoicePreview.propTypes = {
  invoiceNumber: PropTypes.string.isRequired,
  invoiceDate: PropTypes.string.isRequired,
  dueDate: PropTypes.string,
  billingDetails: PropTypes.object,
  lines: PropTypes.array.isRequired,
  setOffEnabled: PropTypes.bool.isRequired,
  requestReceivedDate: PropTypes.string,
  fundingMonthKey: PropTypes.string,
  growthInvestment: PropTypes.number,
  growthInvestments: PropTypes.arrayOf(
    PropTypes.shape({
      requestReceivedDate: PropTypes.string,
      fundingMonthKey: PropTypes.string,
      growthInvestment: PropTypes.number,
    })
  ),
  purchaseAmount: PropTypes.number,
  setOffCreatedDate: PropTypes.string,
  signature: PropTypes.shape({
    dataUrl: PropTypes.string,
    signerName: PropTypes.string,
    signedAt: PropTypes.string,
  }),
  pdfMode: PropTypes.bool,
};

export default InvoicePreview;
